import { Select, SelectContent, SelectItem, SelectTrigger, TextInput } from 'component-library';
import { Control, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Calendar } from '@/components/widgets/a/Calendar/Calendar';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from './Form';

const REQUIRED_TRANSLATION_KEY = 'global.ui.form.input.required';

const useFieldErrorMessage = <V extends FieldValues>(name: FieldPath<V>): string | undefined => {
  const { formState } = useFormContext<V>();
  const errors = formState.errors as Partial<Record<string, { message?: string }>>;
  // eslint-disable-next-line security/detect-object-injection -- name is a typed FieldPath, not user input
  return errors[name]?.message;
};

type BaseFieldProps<V extends FieldValues, N extends FieldPath<V>> = {
  control: Control<V>;
  name: N;
  label: string;
};

export const TextFormField = <V extends FieldValues, N extends FieldPath<V>>({
  control,
  name,
  label,
}: BaseFieldProps<V, N>) => {
  const { t } = useTranslation();
  const errorMessage = useFieldErrorMessage<V>(name);

  return (
    <FormField
      control={control}
      name={name}
      rules={{ required: t(REQUIRED_TRANSLATION_KEY) }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label} *</FormLabel>
          <FormControl>
            <TextInput {...field} />
          </FormControl>
          <FormMessage>{errorMessage}</FormMessage>
        </FormItem>
      )}
    />
  );
};

export const NumericFormField = <V extends FieldValues, N extends FieldPath<V>>({
  control,
  name,
  label,
  invalidNumberMessage,
}: BaseFieldProps<V, N> & { invalidNumberMessage: string }) => {
  const { t } = useTranslation();
  const errorMessage = useFieldErrorMessage<V>(name);

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: t(REQUIRED_TRANSLATION_KEY),
        validate: (value: string) => Number.isFinite(Number(value)) || invalidNumberMessage,
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label} *</FormLabel>
          <FormControl>
            <TextInput {...field} inputMode='numeric' />
          </FormControl>
          <FormMessage>{errorMessage}</FormMessage>
        </FormItem>
      )}
    />
  );
};

export const OptionalNumericFormField = <V extends FieldValues, N extends FieldPath<V>>({
  control,
  name,
  label,
  invalidNumberMessage,
  placeholder,
}: BaseFieldProps<V, N> & { invalidNumberMessage: string; placeholder?: string }) => {
  const errorMessage = useFieldErrorMessage<V>(name);

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        validate: (value: string) => value === '' || Number.isFinite(Number(value)) || invalidNumberMessage,
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <TextInput {...field} inputMode='numeric' placeholder={placeholder} />
          </FormControl>
          <FormMessage>{errorMessage}</FormMessage>
        </FormItem>
      )}
    />
  );
};

export const DateFormField = <V extends FieldValues, N extends FieldPath<V>>({
  control,
  name,
  label,
  min,
}: BaseFieldProps<V, N> & { min?: string }) => {
  const { t } = useTranslation();
  const errorMessage = useFieldErrorMessage<V>(name);

  return (
    <FormField
      control={control}
      name={name}
      rules={{ required: t(REQUIRED_TRANSLATION_KEY) }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label} *</FormLabel>
          <FormControl>
            <Calendar name={field.name} value={field.value} onChange={(v) => field.onChange(v ?? '')} min={min} />
          </FormControl>
          <FormMessage>{errorMessage}</FormMessage>
        </FormItem>
      )}
    />
  );
};

export type SelectFormFieldOption = { value: string; label: string };

export const SelectFormField = <V extends FieldValues, N extends FieldPath<V>>({
  control,
  name,
  label,
  placeholder,
  options,
}: BaseFieldProps<V, N> & { placeholder?: string; options: SelectFormFieldOption[] }) => {
  const { t } = useTranslation();
  const errorMessage = useFieldErrorMessage<V>(name);

  return (
    <FormField
      control={control}
      name={name}
      rules={{ required: t(REQUIRED_TRANSLATION_KEY) }}
      render={({ field: { value, ...field } }) => (
        <FormItem>
          <FormLabel>{label} *</FormLabel>
          <Select value={value} {...field}>
            <FormControl>
              <SelectTrigger placeholder={placeholder} />
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage>{errorMessage}</FormMessage>
        </FormItem>
      )}
    />
  );
};
